import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Film, Trophy } from "lucide-react";
import { API, IMAGES } from "@/config";
import SearchBar from "@/components/SearchBar";

export default function VaultHomePage() {
  const [coachEras, setCoachEras] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchCoachEras();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch
  
  const fetchCoachEras = async () => {
    try {
      const res = await axios.get(`${API}/coach-eras`);
      setCoachEras(res.data);
    } catch (err) {
      console.error("Error fetching coach eras:", err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return (
    <>
      <section className="vault-hero">
        <div style={{ backgroundImage: `url('${IMAGES.vaultBackground}')`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.25, position: 'absolute', inset: 0 }}></div>
        <div className="vault-hero-content">
          <h1 className="vault-hero-title">The Vault</h1>
          <p className="vault-hero-subtitle">
            Relive the greatest moments in Kentucky Wildcats history. 
            Browse basketball by coaching era, plus TBT and Football.
          </p>
        </div>
      </section>
      
      <section className="container" style={{ padding: '4rem 1rem' }}>
        <div className="section-header">
          <h2 className="section-title">Browse by Coach</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchBar />
            <Link to="/vault/documentaries" className="btn btn-secondary" data-testid="vault-documentaries-btn">
              <Film size={16} />
              Documentaries
            </Link>
            <Link to="/vault/tbt" className="btn btn-secondary" data-testid="vault-tbt-btn">
              <Trophy size={16} />
              TBT
            </Link>
            <Link to="/vault/football" className="btn btn-secondary" data-testid="vault-football-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="12" rx="10" ry="7" transform="rotate(45 12 12)"/><path d="M7 17 L17 7"/><path d="M9 9 l1.5 1.5"/><path d="M11 11 l1.5 1.5"/><path d="M13 13 l1.5 1.5"/></svg>
              Football
            </Link>
          </div>
        </div>
        
        {coachEras.length === 0 ? (
          <div className="empty-state">
            <Film className="empty-state-icon" />
            <h3 className="empty-state-title">No Coach Eras Yet</h3>
            <p className="empty-state-text">Check back soon for content!</p>
          </div>
        ) : (
          <div className="coach-era-grid">
            {coachEras.map((era) => (
              <Link 
                key={era.id} 
                to={`/vault/era/${era.id}`} 
                className="coach-era-card"
                data-testid={`coach-era-${era.coach_name}`}
              >
                <div 
                  className="coach-era-card-bg" 
                  style={{ backgroundImage: `url(${era.thumbnail || 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=600&q=80'})` }}
                />
                <div className="coach-era-card-overlay" />
                <div className="coach-era-card-content">
                  <h3 className="coach-era-card-name">{era.coach_name}</h3>
                  <p className="coach-era-card-years">{era.years}</p>
                  {era.championships > 0 && (
                    <span className="coach-era-championships">
                      <Trophy size={14} />
                      {era.championships} {era.championships === 1 ? 'Championship' : 'Championships'}
                    </span>
                  )}
                  <span className="coach-era-card-count">
                    {era.season_count} {era.season_count === 1 ? 'Season' : 'Seasons'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
